import { DOCUMENT } from '@angular/common';
import { Injectable, afterNextRender, inject, signal } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { PrimeNG } from 'primeng/config';
import { TranslateService } from '@ngx-translate/core';
import { languageFromUrl, localizedPath, pageFromUrl } from './language-routes';
import { DEFAULT_LANGUAGE, LocalizedPage, SupportedLanguage } from './language.types';

const STORAGE_KEY = 'soh-language';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly current = signal<SupportedLanguage>(DEFAULT_LANGUAGE);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly primeNG = inject(PrimeNG);
  private browserReady = false;
  private preferred: SupportedLanguage | null = null;

  constructor() {
    // Hydrate the URL's language first, then apply this browser's root-page preference.
    afterNextRender(() => {
      this.browserReady = true;
      try {
        const saved = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
        if (saved === 'ro' || saved === 'en') this.preferred = saved;
      } catch { /* Storage restrictions must not prevent navigation. */ }
      const location = this.document.defaultView?.location;
      const initialUrl = location ? location.pathname + location.search + location.hash : this.router.url;
      const redirect = this.rootPreferenceRedirect(initialUrl);
      if (redirect) void this.router.navigateByUrl(redirect, { replaceUrl: true });
    });
  }

  remember(language: SupportedLanguage): void {
    this.preferred = language;
    try { this.document.defaultView?.localStorage.setItem(STORAGE_KEY, language); } catch { /* Use the in-memory choice. */ }
  }

  path(page: LocalizedPage): string {
    return localizedPath(page, this.current());
  }

  equivalentUrl(language: SupportedLanguage): UrlTree {
    const url = this.router.parseUrl(this.router.url);
    const page = pageFromUrl(this.router.url);
    return this.router.createUrlTree([localizedPath(page ?? 'home', language)], {
      queryParams: url.queryParams, fragment: page ? url.fragment ?? undefined : undefined
    });
  }

  rootPreferenceRedirect(url: string): UrlTree | null {
    const path = url.split(/[?#]/)[0];
    if (!this.browserReady || path !== '/' || this.preferred !== 'en') return null;
    const tree = this.router.parseUrl(url);
    return this.router.createUrlTree(['/en'], { queryParams: tree.queryParams, fragment: tree.fragment ?? undefined });
  }

  setFromUrl(url: string): void {
    this.setLanguage(languageFromUrl(url));
  }

  setLanguage(language: SupportedLanguage): void {
    this.current.set(language);
    this.translate.use(language);
    this.document.documentElement.lang = language;
    this.primeNG.setTranslation({ aria: this.translate.instant('common.primeAria') });
  }
}
